// Custom Yjs provider over WebRTC DataChannels. Drop-in replacement for
// `WebsocketProvider`'s public surface that the rest of the app touches:
//
//   - `awareness` (y-protocols/awareness Awareness instance)
//   - constructor takes a Y.Doc and manages sync via standard y-protocols
//   - destroy() tears down peers and listeners
//   - emits 'peers-changed' for the React layer
//
// Topology: gossip mesh. Each peer can directly connect to any other peer
// via a manual offer/answer signaling round-trip (see signalingCodec.ts).
// Once a DataChannel is open between A and B, all local Yjs updates plus
// awareness deltas are flooded to every direct connection except the one
// they arrived on. Y.applyUpdate carries origin info, so re-broadcast loops
// can't form. For 10 peers in a full mesh this is 45 connections worst-case;
// in practice we expect a tree (everyone connects to whoever invited them)
// which keeps the count to ~9.
//
// What this provider does NOT do (deferred):
//   - TURN relay for symmetric NATs (STUN-only via public servers)
//   - Automatic peer discovery; every new connection needs a paste-back
//   - Re-connection / NACK on data-channel close

import * as Y from 'yjs';
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness';
// `removeAwarenessStates` is used in the beforeunload handler to clear our
// OWN state on tab close (so peers don't see a frozen cursor).
import { readSyncMessage, writeSyncStep1, writeUpdate } from 'y-protocols/sync';
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';
import { Observable } from 'lib0/observable';
import { encodeSignaling, decodeSignaling } from './signalingCodec';
import { MAX_PEERS, type PeerInfo, type SignalingPayload } from './types';

// Public STUN-only ICE config. Symmetric NATs (~10–20% of residential users)
// will silently fail to connect; documented in the ADR. TURN is out of scope
// for v1 (would require hosting `coturn`).
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

// y-protocols message types. Pinned to constants so the wire format is
// stable even if we add custom message types later.
const MESSAGE_SYNC = 0;
const MESSAGE_AWARENESS = 1;
// MESSAGE_QUERY_AWARENESS = 3 — included for parity with y-websocket but unused.

type ProviderEvents = {
  'peers-changed': (peers: PeerInfo[]) => void;
};

// Each invite the local peer has open. The RTCPeerConnection is created the
// moment the user clicks "Invite", before the guest opens the URL, so the
// SDP can be encoded into the share link. `offerCode` is only present on the
// host side (this peer generated the offer); guest-side pending entries (we
// generated the answer and are waiting for the inviter to accept it) have
// an empty offerCode and are excluded from the visible "Pending invites"
// list in the UI.
interface PendingInvite {
  peerId: string;
  pc: RTCPeerConnection;
  offerCode: string;
  createdAt: number;
}

// Each direct WebRTC neighbor.
interface Neighbor {
  peerId: string;
  pc: RTCPeerConnection;
  channel: RTCDataChannel;
  // True once the data channel has opened AND the initial sync handshake
  // (sync-step-1 + sync-step-2) has completed at least once. Used by the
  // UI to distinguish "negotiating" from "live".
  synced: boolean;
  connectedAt: number;
}

// Internal mapping from a Y.Doc update origin tag back to the neighbor that
// produced it, so re-broadcast skips that neighbor.
const ORIGIN_PREFIX = 'p2p:';

export class P2pProvider extends Observable<keyof ProviderEvents> {
  readonly doc: Y.Doc;
  readonly awareness: Awareness;
  readonly peerId: string;

  private neighbors = new Map<string, Neighbor>();
  private pendingInvites = new Map<string, PendingInvite>();

  private docUpdateHandler: (update: Uint8Array, origin: unknown) => void;
  private awarenessUpdateHandler: (changes: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => void;
  private beforeUnloadHandler: () => void;

  constructor(doc: Y.Doc, peerId: string) {
    super();
    this.doc = doc;
    this.peerId = peerId;
    this.awareness = new Awareness(doc);

    // When the local doc changes, flood the update to every neighbor that
    // didn't produce it. Origin is the neighbor's peerId string (prefixed
    // to avoid colliding with caller-supplied origins like 'local-drag').
    this.docUpdateHandler = (update, origin) => {
      const skip = typeof origin === 'string' && origin.startsWith(ORIGIN_PREFIX)
        ? origin.slice(ORIGIN_PREFIX.length)
        : null;
      this.broadcastSyncUpdate(update, skip);
    };
    this.doc.on('update', this.docUpdateHandler);

    // Same for awareness.
    this.awarenessUpdateHandler = (changes, origin) => {
      const skip = typeof origin === 'string' && origin.startsWith(ORIGIN_PREFIX)
        ? origin.slice(ORIGIN_PREFIX.length)
        : null;
      const changedClients = [...changes.added, ...changes.updated, ...changes.removed];
      this.broadcastAwarenessUpdate(changedClients, skip);
    };
    this.awareness.on('update', this.awarenessUpdateHandler);

    // Clear local awareness before tab close so peers see us drop instead
    // of carrying a ghost cursor until their next connection check.
    this.beforeUnloadHandler = () => {
      removeAwarenessStates(this.awareness, [this.doc.clientID], 'window unload');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  // -------- public API: invite / accept --------

  // Generate a fresh offer and return the URL-safe code (no protocol prefix).
  // Caller is responsible for wrapping into a join URL.
  async createInvite(): Promise<{ peerId: string; offerCode: string }> {
    if (this.neighbors.size + this.pendingInvites.size >= MAX_PEERS) {
      throw new Error(`Max peers (${MAX_PEERS}) reached. Wait for a guest to connect or remove an invite.`);
    }
    const invitePeerId = randomPeerId();
    const pc = this.createPeerConnection();
    const channel = pc.createDataChannel('frame-sync', { ordered: true });

    // Hook everything up before negotiation; nothing fires until the remote
    // answer is applied and ICE completes.
    this.wireChannel(invitePeerId, channel, pc);
    pc.addEventListener('connectionstatechange', () => this.handleConnectionStateChange(invitePeerId, pc));

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const sdp = await waitForIceComplete(pc);

    const payload: SignalingPayload = {
      v: 1,
      kind: 'offer',
      peerId: this.peerId, // the inviter's peerId so the joiner knows whom they're joining
      sdp,
    };
    const offerCode = encodeSignaling(payload);
    this.pendingInvites.set(invitePeerId, {
      peerId: invitePeerId,
      pc,
      offerCode,
      createdAt: Date.now(),
    });
    return { peerId: invitePeerId, offerCode };
  }

  // Apply an answer code returned by the guest. The pending invite is keyed
  // by a local ephemeral id (createInvite); we keep that id as the
  // connection identifier. The guest's reported peerId is only used inside
  // their own awareness payload for display.
  async acceptAnswer(answerCode: string): Promise<void> {
    const payload = decodeSignaling(answerCode);
    if (payload.kind !== 'answer') throw new Error('Expected an answer code, got an offer.');

    const pending = this.oldestPendingInvite();
    if (!pending) throw new Error('No pending invite to apply this answer to.');

    await pending.pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
  }

  // Guest side: parse an offer code, build a connection, return our answer
  // code. The new neighbor is recorded as pending until the data channel
  // opens.
  async respondToInvite(offerCode: string): Promise<{ inviterPeerId: string; answerCode: string }> {
    if (this.neighbors.size >= MAX_PEERS) {
      throw new Error(`Max peers (${MAX_PEERS}) reached on this end.`);
    }
    const offerPayload = decodeSignaling(offerCode);
    if (offerPayload.kind !== 'offer') throw new Error('Expected an offer code, got an answer.');

    const pc = this.createPeerConnection();
    const inviterPeerId = offerPayload.peerId;

    pc.addEventListener('datachannel', (e) => {
      this.wireChannel(inviterPeerId, e.channel, pc);
    });
    pc.addEventListener('connectionstatechange', () => this.handleConnectionStateChange(inviterPeerId, pc));

    await pc.setRemoteDescription({ type: 'offer', sdp: offerPayload.sdp });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    const sdp = await waitForIceComplete(pc);

    // Park as pending so destroy() can clean it up if the user never pastes
    // back. We use the inviter's peerId because we know it from the offer.
    // Empty offerCode marks this as guest-side (not surfaced in UI).
    this.pendingInvites.set(inviterPeerId, {
      peerId: inviterPeerId,
      pc,
      offerCode: '',
      createdAt: Date.now(),
    });

    const answerPayload: SignalingPayload = {
      v: 1,
      kind: 'answer',
      peerId: this.peerId,
      sdp,
    };
    return { inviterPeerId, answerCode: encodeSignaling(answerPayload) };
  }

  // Drop an invite the user generated but never shared. Releases the
  // underlying RTCPeerConnection.
  cancelInvite(peerId: string): void {
    const pending = this.pendingInvites.get(peerId);
    if (!pending) return;
    pending.pc.close();
    this.pendingInvites.delete(peerId);
  }

  // Drop a connected neighbor. The neighbor's awareness state (cursor,
  // selection) will time out via y-protocols' own outdated-state timer
  // (~30s) — we don't track which clientIDs came from which neighbor.
  removePeer(peerId: string): void {
    const neighbor = this.neighbors.get(peerId);
    if (!neighbor) return;
    neighbor.channel.close();
    neighbor.pc.close();
    this.neighbors.delete(peerId);
    this.emit('peers-changed', [this.listPeers()]);
  }

  listPeers(): PeerInfo[] {
    return [...this.neighbors.values()].map((n) => ({
      peerId: n.peerId,
      connectedAt: n.connectedAt,
    }));
  }

  listPendingInvites(): { peerId: string; offerCode: string; createdAt: number }[] {
    return [...this.pendingInvites.values()]
      .filter((p) => !!p.offerCode) // hide guest-side pending (no offer to show)
      .map((p) => ({ peerId: p.peerId, offerCode: p.offerCode, createdAt: p.createdAt }));
  }

  // -------- teardown --------

  override destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    this.doc.off('update', this.docUpdateHandler);
    this.awareness.off('update', this.awarenessUpdateHandler);
    for (const pending of this.pendingInvites.values()) pending.pc.close();
    this.pendingInvites.clear();
    for (const n of this.neighbors.values()) {
      n.channel.close();
      n.pc.close();
    }
    this.neighbors.clear();
    this.awareness.destroy();
    super.destroy();
  }

  // -------- internals --------

  private createPeerConnection(): RTCPeerConnection {
    return new RTCPeerConnection({ iceServers: ICE_SERVERS });
  }

  private oldestPendingInvite(): PendingInvite | null {
    let best: PendingInvite | null = null;
    for (const p of this.pendingInvites.values()) {
      if (!p.offerCode) continue; // skip guest-side pending
      if (!best || p.createdAt < best.createdAt) best = p;
    }
    return best;
  }

  private wireChannel(peerId: string, channel: RTCDataChannel, pc: RTCPeerConnection): void {
    channel.binaryType = 'arraybuffer';

    channel.addEventListener('open', () => {
      this.pendingInvites.delete(peerId);
      const neighbor: Neighbor = {
        peerId,
        pc,
        channel,
        synced: false,
        connectedAt: Date.now(),
      };
      this.neighbors.set(peerId, neighbor);

      // Send sync step 1 (state vector) + a snapshot of our awareness so the
      // new neighbor immediately learns about everyone we see.
      const syncMsg = encoding.createEncoder();
      encoding.writeVarUint(syncMsg, MESSAGE_SYNC);
      writeSyncStep1(syncMsg, this.doc);
      this.sendTo(neighbor, encoding.toUint8Array(syncMsg));

      const awarenessIds = [...this.awareness.getStates().keys()];
      if (awarenessIds.length > 0) {
        const awMsg = encoding.createEncoder();
        encoding.writeVarUint(awMsg, MESSAGE_AWARENESS);
        encoding.writeVarUint8Array(awMsg, encodeAwarenessUpdate(this.awareness, awarenessIds));
        this.sendTo(neighbor, encoding.toUint8Array(awMsg));
      }

      this.emit('peers-changed', [this.listPeers()]);
    });

    channel.addEventListener('message', (e: MessageEvent) => {
      this.handleMessage(peerId, e.data as ArrayBuffer);
    });

    channel.addEventListener('close', () => {
      this.neighbors.delete(peerId);
      this.emit('peers-changed', [this.listPeers()]);
    });

    channel.addEventListener('error', () => {
      // Errors usually precede 'close'; the close handler does the cleanup.
    });
  }

  private handleConnectionStateChange(peerId: string, pc: RTCPeerConnection): void {
    if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
      this.neighbors.delete(peerId);
      this.pendingInvites.delete(peerId);
      pc.close();
      this.emit('peers-changed', [this.listPeers()]);
    }
  }

  private handleMessage(peerId: string, data: ArrayBuffer): void {
    const neighbor = this.neighbors.get(peerId);
    if (!neighbor) return;

    const decoder = decoding.createDecoder(new Uint8Array(data));
    const messageType = decoding.readVarUint(decoder);

    if (messageType === MESSAGE_SYNC) {
      const encoder = encoding.createEncoder();
      encoding.writeVarUint(encoder, MESSAGE_SYNC);
      const syncMessageType = readSyncMessage(decoder, encoder, this.doc, ORIGIN_PREFIX + peerId);
      // Only send back if readSyncMessage produced output (sync step 1 reply
      // and sync step 2 reply do; pure update consumption produces nothing).
      if (encoding.length(encoder) > 1) {
        this.sendTo(neighbor, encoding.toUint8Array(encoder));
      }
      // syncMessageType 1 (step2) or 2 (update) means the remote sent us doc
      // state — mark this neighbor as synced.
      if (syncMessageType === 1 || syncMessageType === 2) {
        if (!neighbor.synced) {
          neighbor.synced = true;
          this.emit('peers-changed', [this.listPeers()]);
        }
      }
      return;
    }

    if (messageType === MESSAGE_AWARENESS) {
      const update = decoding.readVarUint8Array(decoder);
      applyAwarenessUpdate(this.awareness, update, ORIGIN_PREFIX + peerId);
      return;
    }

    // Unknown message types are ignored — forward-compatible.
  }

  private broadcastSyncUpdate(update: Uint8Array, skipPeerId: string | null): void {
    if (this.neighbors.size === 0) return;
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    writeUpdate(encoder, update);
    const bytes = encoding.toUint8Array(encoder);
    for (const neighbor of this.neighbors.values()) {
      if (neighbor.peerId === skipPeerId) continue;
      this.sendTo(neighbor, bytes);
    }
  }

  private broadcastAwarenessUpdate(changedClients: number[], skipPeerId: string | null): void {
    if (this.neighbors.size === 0 || changedClients.length === 0) return;
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_AWARENESS);
    encoding.writeVarUint8Array(encoder, encodeAwarenessUpdate(this.awareness, changedClients));
    const bytes = encoding.toUint8Array(encoder);
    for (const neighbor of this.neighbors.values()) {
      if (neighbor.peerId === skipPeerId) continue;
      this.sendTo(neighbor, bytes);
    }
  }

  private sendTo(neighbor: Neighbor, bytes: Uint8Array): void {
    if (neighbor.channel.readyState !== 'open') return;
    try {
      // Cast: lib0 returns Uint8Array<ArrayBufferLike>; RTCDataChannel.send
      // requires a strict ArrayBuffer-backed view. They're interchangeable
      // at runtime — the type narrowing is purely TS structural.
      neighbor.channel.send(bytes as Uint8Array<ArrayBuffer>);
    } catch {
      // Channel closed between our readyState check and send — ignore;
      // the 'close' handler will clean up.
    }
  }
}

function randomPeerId(): string {
  // 12 lowercase alphanumerics. Collision space is 36^12 ≈ 4.7e18, more
  // than enough to dedupe a few invites in flight at once.
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 12; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

// Wait for ICE gathering to complete and return the final local-description
// SDP (with all candidates bundled). Non-trickle mode keeps signaling to a
// single round-trip at the cost of a slightly slower connect.
async function waitForIceComplete(pc: RTCPeerConnection): Promise<string> {
  if (pc.iceGatheringState === 'complete') return pc.localDescription!.sdp;
  return new Promise<string>((resolve) => {
    const onChange = () => {
      if (pc.iceGatheringState !== 'complete') return;
      pc.removeEventListener('icegatheringstatechange', onChange);
      resolve(pc.localDescription!.sdp);
    };
    pc.addEventListener('icegatheringstatechange', onChange);
  });
}
